/**
 * Playing against a zone server (docs/ONLINE.md). The tab keeps its local `World` as a replica the
 * renderer and HUD read as before, but the rules run on the server: commands are sent there, and
 * the replica's entities are written from the server's snapshots, played back a little behind the
 * newest so movement stays smooth between them (interpolation). Events come from the server too,
 * released when playback reaches the snapshot that brought them.
 *
 * Your character's progress (arts, skill bar, gold, bag, quests) comes from the server (`progress`
 * messages, on joining and when it changes) and is written over the replica's, except where the
 * replica's own presentation lives (zone, indoors, cutscenes seen). Other players are drawn as the
 * server sends them (figure, worn looks, name: net/snapshot.ts). A typed line (`zoneSay`) also
 * pops a bubble over its speaker. A dropped connection is retried a few times (the server holds the
 * character for a minute); a refusal (full, bad name) is not.
 *
 * Anything the server shows that the replica did not spawn (other players, a bout's foe, a room's
 * people and monsters, a monster respawned) is drawn as a stand-in under the server's id, so selecting,
 * talking and striking reach the server's entity (net/snapshot.ts `syncStandIns`). The warehouse
 * window follows the server's keeper (`warehouse` events).
 *
 * Limits: no prediction yet (your own character is shown like everyone else, a snapshot interval
 * behind); the replica's own HUD state of a bout (World `duel`) is not mirrored, only its events.
 */
import {
  advanceReplica,
  applyEntities,
  blendEntities,
  decodeServerMessage,
  encodeMessage,
  parseProgress,
  patchEntities,
  PROTOCOL_VERSION,
  syncStandIns,
  warehouseOf,
  type Appearance,
  type ClientMessage,
  type NetEntity,
  type PlayerCommand,
  type PlayerProgress,
  type World,
  type WorldEvent,
} from '@nine/shared';

/** Commands played on the replica itself: cutscenes are presentation (the server runs none). */
const LOCAL: ReadonlySet<PlayerCommand['t']> = new Set(['sceneAdvance', 'sceneSkip', 'sceneReplay']);

/** Snapshots kept for playback (a second at 20 Hz). */
const KEEP = 20;

/** Progress fields the replica keeps its own of (where it is drawn, which cutscenes it played). */
const KEEP_LOCAL = ['location', 'arriveFrom', 'indoors', 'scenes'] as const satisfies readonly (keyof PlayerProgress)[];

/** Waits before each reconnection attempt (s); after the last, the player is told to reload. */
const RETRY = [1, 2, 4, 8, 15, 30];

export type NetStatus = 'connecting' | 'open' | 'closed';

/** The replica's own entry points, from before the switch put the network in front of them. */
export interface LocalSide {
  apply(cmd: PlayerCommand): void;
  drainEvents(): WorldEvent[];
}

/** What the hello says about this player (net/switch.ts fills it). */
export interface HelloInfo {
  /** The character key (a dev server's tab key; the open trial's browser key). */
  char?: string;
  /** An invite code or ticket. */
  token?: string;
  /** The open trial's typed name. */
  name?: string;
  look?: Appearance;
}

export class NetWorld {
  status: NetStatus = 'connecting';
  /** Your character's id on the server (null: watching only). */
  you: number | null = null;
  /** The last command the server has applied (for prediction and reconciliation, later). */
  ack = 0;
  /** Round-trip time to the server (ms), from the pings. */
  rtt = 0;
  private ws!: WebSocket;
  private seq = 0;
  private snapHz = 20;
  private ents = new Map<number, NetEntity>();
  private hour: number | undefined;
  /** Received states by server time, oldest first. */
  private states: { time: number; ents: Map<number, NetEntity> }[] = [];
  /** Server events waiting for playback to reach the snapshot that brought them. */
  private pending: { time: number; events: WorldEvent[] }[] = [];
  /** Events of the connection itself (notices, the progress arriving), released at once. */
  private now: WorldEvent[] = [];
  /** The playback clock, in server time; NaN until the first snapshot. */
  private playT = NaN;
  private pingT = 0;
  /** Other players shown as stand-ins on the replica (server ids). */
  private readonly standIns = new Set<number>();
  /** The server refused this player for good (full, bad name, version): no retrying. */
  private refused = false;
  private tries = 0;
  /** Was ever welcomed (a drop after that is a reconnection, said so). */
  private joined = false;

  constructor(
    private readonly world: World,
    private readonly local: LocalSide,
    readonly url: string,
    private readonly hello: HelloInfo = {},
  ) {
    this.connect();
  }

  private connect(): void {
    this.status = 'connecting';
    // A new connection numbers its commands afresh (a re-welcome on the same one does not).
    this.seq = 0;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener('open', () =>
      this.send({
        k: 'hello',
        v: PROTOCOL_VERSION,
        ...(this.hello.char ? { char: this.hello.char } : {}),
        ...(this.hello.token ? { token: this.hello.token } : {}),
        ...(this.hello.name ? { name: this.hello.name } : {}),
        look: this.hello.look ?? this.world.progress.appearance,
      }),
    );
    ws.addEventListener('message', (e) => typeof e.data === 'string' && this.receive(e.data));
    ws.addEventListener('close', (e) => {
      if (ws !== this.ws) return;
      this.status = 'closed';
      console.warn(`[net] disconnected from ${this.url} (${e.code} ${e.reason || 'no reason'})`);
      if (this.refused) return;
      const wait = RETRY[this.tries++];
      if (wait === undefined) return this.notice('หลุดการเชื่อมต่อกับเซิร์ฟเวอร์ · โหลดหน้าใหม่เพื่อเข้าอีกครั้ง');
      if (this.joined && this.tries === 1) this.notice('หลุดการเชื่อมต่อ · กำลังเชื่อมต่อใหม่…');
      setTimeout(() => this.connect(), wait * 1000);
    });
  }

  /** How far behind the newest snapshot playback runs (s): two snapshot intervals. */
  get delay(): number {
    return 2 / this.snapHz;
  }

  apply(cmd: PlayerCommand): void {
    if (LOCAL.has(cmd.t)) return this.local.apply(cmd);
    if (this.status === 'open' && this.you !== null) this.send({ k: 'cmd', seq: ++this.seq, cmd });
  }

  step(dt: number): void {
    advanceReplica(this.world, dt, this.hour);
    // A cutscene under way keeps playing on the replica (its lines, its camera).
    if (this.world.story.active) this.world.story.step(dt);
    if ((this.pingT -= dt) <= 0 && this.status === 'open') {
      this.pingT = 2;
      this.send({ k: 'ping', t: performance.now() });
    }
    const newest = this.states.at(-1);
    if (!newest) return;
    // Playback trails the newest snapshot by `delay`; it eases toward that, and jumps when far off.
    const target = newest.time - this.delay;
    this.playT += dt;
    if (!(Math.abs(target - this.playT) < 0.25)) this.playT = target;
    else this.playT += (target - this.playT) * 0.05;
    const i = this.states.findIndex((s) => s.time > this.playT);
    let shown: Map<number, NetEntity>;
    if (i === -1) shown = newest.ents;
    else if (i === 0) shown = this.states[0]!.ents;
    else {
      const a = this.states[i - 1]!;
      const b = this.states[i]!;
      shown = blendEntities(a.ents, b.ents, (this.playT - a.time) / (b.time - a.time));
    }
    syncStandIns(this.world, shown.values(), this.you, this.standIns);
    applyEntities(this.world, shown.values(), this.you);
  }

  drainEvents(): WorldEvent[] {
    const out = this.local.drainEvents();
    if (this.now.length) out.push(...this.now.splice(0));
    while (this.pending.length && this.pending[0]!.time <= this.playT) {
      for (const ev of this.pending.shift()!.events) {
        out.push(ev);
        // A typed line also shows over its speaker (render/effects.ts draws `bark` bubbles); yours over your figure.
        if (ev.type === 'zoneSay')
          out.push({ type: 'bark', id: ev.id === this.you ? this.world.player.id : ev.id, text: ev.text });
        // The server's store opened or closed: the replica's system holds the keeper the window reads.
        if (ev.type === 'warehouse' && ev.what !== 'changed') {
          const wh = warehouseOf(this.world);
          if (wh) wh.keeper = ev.what === 'open' ? (ev.keeper ?? null) : null;
        }
      }
    }
    return out;
  }

  private notice(text: string): void {
    this.now.push({ type: 'notice', text });
  }

  private send(m: ClientMessage): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeMessage(m));
  }

  /** The server's progress over the replica's (its own presentation fields kept), stats and panels refreshed. */
  private takeProgress(save: string): void {
    const p = parseProgress(save);
    if (!p) return console.warn('[net] unreadable progress from the server');
    const mine = this.world.progress;
    const keep = Object.fromEntries(KEEP_LOCAL.map((k) => [k, mine[k]]));
    for (const k of Object.keys(mine)) if (!(k in p)) delete (mine as unknown as Record<string, unknown>)[k];
    Object.assign(mine, p, keep);
    // Moved into a building (or out of it) on the server: the replica swaps its map the way a door does here.
    const into = p.indoors ?? null;
    const now = this.world.interiors.where;
    if (into?.interior !== now?.interior || into?.floor !== now?.floor || into?.building !== now?.building) {
      syncStandIns(this.world, [], null, this.standIns); // stand-ins are set aside with the zone's people otherwise
      this.world.interiors.follow(into);
      // States from before the move would bring the other place's people in: playback starts again at the next snapshot.
      this.states = [];
      this.playT = NaN;
    }
    this.world.refreshPlayerStats();
    this.now.push({ type: 'progressChanged' });
  }

  private receive(raw: string): void {
    const m = decodeServerMessage(raw);
    if (!m) return console.warn('[net] unreadable message from the server');
    switch (m.k) {
      case 'welcome': {
        // Welcomed again (moved into a duel's World or back, or reconnected): a new set of ids.
        this.ents = new Map();
        this.states = [];
        this.pending = [];
        this.playT = NaN;
        syncStandIns(this.world, [], null, this.standIns);
        if (this.joined && this.tries > 0) this.notice('เชื่อมต่อกลับแล้ว');
        this.status = 'open';
        this.joined = true;
        this.tries = 0;
        this.you = m.you;
        this.snapHz = m.snapHz;
        if (m.zone !== this.world.map.zone)
          console.warn(
            `[net] the server runs ${m.zone}, this tab is in ${this.world.map.zone}: only you will match`,
          );
        console.info(`[net] connected to ${this.url}: ${m.you === null ? 'watching' : `entity ${m.you}`}`);
        break;
      }
      case 'snap':
        this.ack = m.ack;
        this.hour = m.hour;
        this.ents = patchEntities(this.ents, m.ents, m.gone);
        this.states.push({ time: m.time, ents: this.ents });
        if (this.states.length > KEEP) this.states.shift();
        if (m.events.length) this.pending.push({ time: m.time, events: m.events });
        // Not being played back (still on the title screen): old news is dropped, not hoarded.
        if (this.pending.length > KEEP * 5) this.pending.shift();
        break;
      case 'progress':
        this.takeProgress(m.save);
        break;
      case 'pong':
        this.rtt = performance.now() - m.t;
        break;
      case 'error':
        console.warn(`[net] server: ${m.code}: ${m.text}`);
        if (m.fatal) {
          this.refused = true;
          this.notice(m.text);
        }
        break;
    }
  }
}
